import { useCallback } from 'react';

import { useQueryClient } from '@mint-lib/query-client';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

type Payload = { flowId: string; isActive: boolean };

const useChangeStatusMutation = () => {
  const { mutateAsync, isPending } = ApprovalFlows.updateFlowStatus();
  const queryClient = useQueryClient();

  const changeFlowStatus = useCallback(
    async ({ flowId, isActive }: Payload) => {
      const { queryKey } = ApprovalFlows.listFlows({
        data: {
          sort: 'asc',
          options: [],
        },
      });
      const prevQueryData =
        queryClient.getQueryData<ApprovalFlows.FlowListResponse>(queryKey);

      try {
        queryClient.setQueryData<ApprovalFlows.FlowListResponse>(
          queryKey,
          (oldData) => {
            if (!oldData?.data?.items) {
              return oldData;
            }

            return {
              ...oldData,
              data: {
                ...oldData.data,
                items: oldData.data.items.map((flow) => {
                  if (flow.id === flowId) {
                    return {
                      ...flow,
                      archived: !isActive,
                    };
                  }

                  return flow;
                }),
              },
            };
          },
        );

        await mutateAsync({
          urlParams: { flowId },
          data: { archive: !isActive },
        });
        ApprovalFlows.getFlow.invalidate(queryClient, {
          urlParams: {
            flowId,
          },
        });
      } catch {
        queryClient.setQueryData<ApprovalFlows.FlowListResponse>(
          queryKey,
          prevQueryData,
        );
        throw new Error('Flow status updating failed');
      }
    },
    [mutateAsync, queryClient],
  );

  return {
    changeFlowStatus,
    isPending,
  };
};

export default useChangeStatusMutation;
